import React from 'react';
import { motion } from 'framer-motion';
export interface VocabularyWord {
  id: string;
  hiragana: string;
  kanji: string;
  romaji: string;
  pitchPattern: number[]; // 0 for low, 1 for high
  meaning: string;
  mnemonic: string;
  exampleSentence: string;
  exampleTranslation: string;
}
interface VocabularyCardProps {
  word: VocabularyWord;
  direction: number;
}
export function VocabularyCard({
  word,
  direction
}: VocabularyCardProps) {
  return <motion.div key={word.id} initial={{
    x: direction * 50,
    opacity: 0
  }} animate={{
    x: 0,
    opacity: 1
  }} exit={{
    x: direction * -50,
    opacity: 0
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 w-full max-w-sm mx-auto flex flex-col items-center text-center relative overflow-hidden" data-id="element-39">
      {/* Decorative background element */}
      <div className="absolute top-0 left-0 w-full h-2 bg-[#E63946]" data-id="element-40" />

      {/* Main Word Section */}
      <div className="mb-8 w-full" data-id="element-41">
        <h2 className="text-5xl font-bold text-gray-900 mb-2 tracking-wide font-sans" data-id="element-42">
          {word.kanji}
        </h2>
        <div className="flex items-center justify-center gap-3 mb-4" data-id="element-43">
          <span className="text-2xl text-gray-400 font-medium" data-id="element-44">
            {word.hiragana}
          </span>
        </div>
        <div className="h-16 flex items-center justify-center gap-1 relative mb-2" data-id="element-45">
          {/* Pitch dots */}
          {word.pitchPattern.map((pitch, index) => <div key={index} className="flex flex-col items-center z-10 w-10" style={{
          marginTop: pitch === 1 ? '-25px' : '25px'
        }} data-id="element-46">
              <div className={`
                w-4 h-4 rounded-full border-2 transition-colors duration-300
                ${pitch === 1 ? 'bg-[#E63946] border-[#E63946]' : 'bg-white border-gray-300'}
              `} data-id="element-47" />
              <span className="text-xs text-gray-400 mt-2 font-mono" data-id="element-48">
                {word.hiragana[index]}
              </span>
            </div>)}
        </div>
        <p className="text-xs text-gray-400 uppercase tracking-widest mt-2" data-id="element-49">
          Pitch Accent
        </p>
      </div>

      {/* Meaning Section */}
      <div className="mb-8 w-full border-t border-gray-100 pt-6" data-id="element-50">
        <h3 className="text-2xl font-bold text-gray-800 mb-1" data-id="element-51">
          {word.meaning}
        </h3>
      </div>

      {/* Mnemonic Section */}
      <div className="bg-[#FDFBF7] rounded-xl p-5 w-full text-left border border-orange-100/50" data-id="element-52">
        <p className="text-xs font-bold text-[#E63946] uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-53">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" data-id="element-54">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" data-id="element-55" />
          </svg>
          Mnemonic
        </p>
        <p className="text-gray-700 text-sm leading-relaxed" data-id="element-56">{word.mnemonic}</p>
      </div>

      {/* Example Sentence */}
      <div className="mt-6 w-full text-center" data-id="element-57">
        <p className="text-lg text-gray-700 mb-1" data-id="element-58">
          "{word.exampleSentence}"
        </p>
        <p className="text-sm text-gray-400" data-id="element-59">{word.exampleTranslation}</p>
      </div>
    </motion.div>;
}